import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './StudentRanking.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Table from '../../../components/table/Table';
import DropDownButton from '../../../components/dropDownButton/DropDownButton';
import ProfilePicture from '../../../components/profilePicture/ProfilePicture';
import FilterGroup from '../../../components/filterGroup/FilterGroup';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useProgram } from '../../../contexts/ProgramContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const EMPTY_FILTERS = {
  clinicalSiteIds: [],
  instructorIds: [],
  yearIds: [],
  semesterIds: [],
  monthIds: [],
};

const POPUP_WIDTH = 240;
const SPACING = 8;

const StudentRanking = () => {
  const { showToast } = useToasts();
  const { programs, selectedProgramId, setSelectedProgramId } = useProgram();
  const [rankings, setRankings] = useState([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activePopup, setActivePopup] = useState(null);
  const popupRef = useRef(null);

  // Filter data
  const [clinicalSites, setClinicalSites] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [years, setYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [months, setMonths] = useState([]);

  const fetchFilterData = useCallback(async () => {
    if (!selectedProgramId) return;
    try {
      const [sitesRes, instructorsRes, yearsRes] = await Promise.all([
        axiosClient.get(`/programs/${selectedProgramId}/clinical-sites`),
        axiosClient.get('users/instructors'),
        axiosClient.get('years/details'),
      ]);

      setClinicalSites(sitesRes.data?.data || sitesRes.data?.clinicalSites || (Array.isArray(sitesRes.data) ? sitesRes.data : []));
      setInstructors(instructorsRes.data?.instructors || instructorsRes.data?.data || (Array.isArray(instructorsRes.data) ? instructorsRes.data : []));

      const yearsData = yearsRes.data || [];
      setYears(yearsData.map(item => item.year));

      const allSemesters = [];
      const allMonths = [];
      const seenSemesters = new Set();
      const seenMonths = new Set();
      yearsData.forEach(item => {
        (item.semesters || []).forEach(sem => {
          if (!seenSemesters.has(sem.id)) {
            seenSemesters.add(sem.id);
            allSemesters.push(sem);
          }
          (sem.months || []).forEach(month => {
            if (!seenMonths.has(month.id)) {
              seenMonths.add(month.id);
              allMonths.push(month);
            }
          });
        });
      });
      setSemesters(allSemesters);
      setMonths(allMonths);
    } catch (error) {
      showToast(error.message, 'error');
    }
  }, [selectedProgramId, showToast]);

  const fetchRankings = useCallback(async () => {
    if (!selectedProgramId) return;
    setIsLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filters.clinicalSiteIds.length) params.clinicalSiteIds = filters.clinicalSiteIds;
      if (filters.instructorIds.length) params.instructorIds = filters.instructorIds;
      if (filters.yearIds.length) params.yearIds = filters.yearIds;
      if (filters.semesterIds.length) params.semesterIds = filters.semesterIds;
      if (filters.monthIds.length) params.monthIds = filters.monthIds;

      const response = await axiosClient.get(`/programs/${selectedProgramId}/student-rankings`, { params });
      setRankings(response.data?.rankings || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProgramId, search, filters, showToast]);

  useEffect(() => { fetchFilterData(); }, [fetchFilterData]);
  useEffect(() => { fetchRankings(); }, [fetchRankings]);

  useEffect(() => {
    if (!activePopup) return;
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setActivePopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePopup]);

  const openPopup = (e, key, title, type, items) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.right + SPACING + POPUP_WIDTH > window.innerWidth
      ? rect.left - POPUP_WIDTH - SPACING
      : rect.right + SPACING;
    const opensUp = rect.top > window.innerHeight / 2;
    const top = rect.top + window.scrollY;
    setActivePopup({ key, title, type, items, position: { top, left: left + window.scrollX }, opensUp });
  };

  const filterCategories = [
    {
      id: 'clinicalSiteIds',
      label: 'Clinical Site',
      data: clinicalSites,
      getLabel: (item) => item.name,
    },
    {
      id: 'instructorIds',
      label: 'Instructor',
      data: instructors,
      getLabel: (item) => `${item.firstName} ${item.lastName}`,
      type: 'user',
    },
    {
      id: 'yearIds',
      label: 'Year',
      data: years,
      getLabel: (item) => item.year,
    },
    {
      id: 'semesterIds',
      label: 'Semester',
      data: semesters,
      getLabel: (item) => item.semester,
    },
    {
      id: 'monthIds',
      label: 'Month',
      data: months,
      getLabel: (item) => item.month,
    },
  ];

  const programOptions = programs.map(p => ({
    label: p.title,
    onClick: () => setSelectedProgramId(p.id),
  }));

  const selectedProgramTitle = programs.find(p => p.id === selectedProgramId)?.title || 'Select Program';

  const columns = [
    {
      header: 'Student',
      key: 'student',
      render: (row) => (
        <div className={styles.studentCell}>
          <ProfilePicture
            firstName={row.firstName}
            lastName={row.lastName}
            src={row.profilePicture}
            size="xs"
          />
          <span>{row.firstName} {row.lastName}</span>
        </div>
      ),
    },
    { header: 'Rank', key: 'rank' },
    {
      header: 'Clinical Site',
      key: 'clinicalSites',
      render: (row) => {
        const sites = row.clinicalSites || [];
        const firstSite = sites[0];
        const extraSites = sites.length - 1;
        return (
          <div
            className={styles.clinicalSiteValue}
            onClick={(e) => sites.length > 1 && openPopup(e, `site-${row.id}`, 'Clinical Sites', 'site', sites)}
            style={{ cursor: sites.length > 1 ? 'pointer' : 'default' }}
          >
            {firstSite ? (
              <>
                <span className={styles.siteName}>{firstSite.name}</span>
                {extraSites > 0 && <span className={styles.extraCount}>+{extraSites}</span>}
              </>
            ) : (
              <span className={styles.siteName}>-</span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Instructor',
      key: 'instructors',
      render: (row) => {
        const insts = row.instructors || [];
        const visibleInstructors = insts.slice(0, 3);
        const extraInstructors = insts.length - 3;
        return (
          <div className={styles.instructorAvatarsWrapper}>
            <div
              className={styles.instructorAvatars}
              onClick={(e) => insts.length > 0 && openPopup(e, `inst-${row.id}`, 'Instructors', 'instructor', insts)}
            >
              {visibleInstructors.map(inst => (
                <div key={inst.id} className={styles.avatarWrapper}>
                  <ProfilePicture
                    firstName={inst.firstName}
                    lastName={inst.lastName}
                    src={inst.profilePicture}
                    size="xs"
                  />
                </div>
              ))}
              {extraInstructors > 0 && <span className={styles.extraCount}>+{extraInstructors}</span>}
              {insts.length === 0 && <span className={styles.siteName}>-</span>}
            </div>
          </div>
        );
      },
    },
    {
      header: 'Current Year',
      key: 'currentYear',
      render: (row) => row.currentYear?.year || '-',
    },
    {
      header: 'Current Month',
      key: 'currentMonth',
      render: (row) => row.currentMonth?.month || '-',
    },
    {
      header: 'Current Semester',
      key: 'currentSemester',
      render: (row) => row.currentSemester?.semester || '-',
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <PageTitle label="Student Ranking" />
          <DropDownButton
            label={selectedProgramTitle}
            items={programOptions}
            type="button"
            size="small"
            variant="primary-light"
            position="bottom-left"
          />
        </div>
        <div className={styles.headerActions}>
          <FilterGroup
            categories={filterCategories}
            filters={filters}
            onChange={setFilters}
          />
          <Search onSubmit={setSearch} />
        </div>
      </div>
      <div className={styles.container}>
        <Table
          columns={columns}
          data={rankings}
          emptyMessage="No student rankings found for this program."
        />
      </div>

      {activePopup && createPortal(
        <div
          className={`${styles.instructorPopup} ${activePopup.opensUp ? styles.opensUp : ''}`}
          ref={popupRef}
          style={activePopup.position}
        >
          <div className={styles.popupHeader}>
            <span className={styles.popupTitle}>{activePopup.title}</span>
            <button className={styles.popupClose} onClick={() => setActivePopup(null)}>✕</button>
          </div>
          <div className={styles.popupList}>
            {activePopup.type === 'instructor' ? (
              activePopup.items.map(inst => (
                <div key={inst.id} className={styles.popupItem}>
                  <ProfilePicture
                    firstName={inst.firstName}
                    lastName={inst.lastName}
                    src={inst.profilePicture}
                    size="xsm"
                  />
                  <span className={styles.popupName}>{inst.firstName} {inst.lastName}</span>
                </div>
              ))
            ) : (
              activePopup.items.map(site => (
                <div key={site.id} className={styles.popupItem}>
                  <span className={styles.popupName}>{site.name}</span>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default StudentRanking;
